import { FC, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { IconArrowRightBold } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { useUBL } from '@common/log-manager';
import { Typography } from '@common/styles';
import { BeautyIngredientSummary, useActiveTabTypeAtom, usePdpConfig, useProductPreviewAtiveTabType } from '@domains/pdp-common';

import { IngredientType, useBeautyService } from '../../services/useBeautyService';
import { IngredientDescriptionItem } from './IngredientDescriptionItem';

const INGREDIENT_TYPES = {
  primary: {
    subject: '추천 성분',
    color: vars.v3Color.skyBlue300,
  },
  warning: {
    subject: '주의 성분',
    color: vars.v3Color.yellow300,
  },
};

interface Props {
  summary: BeautyIngredientSummary;
}

export const IngredientDescription: FC<React.PropsWithChildren<Props>> = ({ summary }) => {
  const { primary_description_list, warning_description_list } = summary;
  const hasPrimary = primary_description_list.length > 0;
  const hasWarning = warning_description_list.length > 0;

  const {
    query: { catalog_product_id },
  } = useRouter();
  const { isProductPreview } = usePdpConfig();
  const [, updatePreviewTab] = useProductPreviewAtiveTabType();
  const [, updateActiveTab] = useActiveTabTypeAtom();
  const { ref: more_ref, inView: in_view } = useInView({ threshold: 0.5 });
  const sendUBL = useUBL();
  const { updateActiveIngredientItem } = useBeautyService();

  const more_ubl = useMemo(
    () => ({
      navigation: 'product_detail',
      object_section: 'beauty_ingridient',
      object_button: 'button',
      object_id: 'see_more',
      data: {
        original_product_id: catalog_product_id ? String(catalog_product_id) : '',
      },
    }),
    [catalog_product_id],
  );

  useEffect(() => {
    if (in_view) {
      sendUBL({
        category: 'impression',
        ...more_ubl,
      });
    }
  }, [sendUBL, more_ubl, in_view]);

  const updateTab = (tab: 'BEAUTY_INGREDIENT') => {
    isProductPreview ? updatePreviewTab(tab) : updateActiveTab(tab);
  };

  const handleMoreClick = () => {
    sendUBL({
      category: 'click',
      ...more_ubl,
    });
    updateTab('BEAUTY_INGREDIENT');
  };

  const handleItemClick = (type: IngredientType) => (idx: number) => {
    updateTab('BEAUTY_INGREDIENT');
    updateActiveIngredientItem(type, idx);
  };

  if (!hasPrimary && !hasWarning) {
    return null;
  }

  const is_show_line_bar = hasPrimary && hasWarning;

  return (
    <Root>
      <Title>
        <p className={Typography.BODY_15_SEMIBOLD}>목적별 성분</p>
        <ShowMore ref={more_ref} className={Typography.BODY_13_SEMIBOLD} onClick={handleMoreClick}>
          더 보기
          <IconArrowRightBold size={16} v3Color='gray400' />
        </ShowMore>
      </Title>
      <List>
        {hasPrimary && (
          <IngredientDescriptionItem
            subject={INGREDIENT_TYPES.primary.subject}
            color={INGREDIENT_TYPES.primary.color}
            descriptions={primary_description_list}
            onClick={handleItemClick('primary')}
          />
        )}
        {is_show_line_bar && <Bar />}
        {hasWarning && (
          <IngredientDescriptionItem
            subject={INGREDIENT_TYPES.warning.subject}
            color={INGREDIENT_TYPES.warning.color}
            descriptions={warning_description_list}
            onClick={handleItemClick('warning')}
          />
        )}
      </List>
    </Root>
  );
};

const Root = styled.div`
  border: 1px solid ${vars.v3Color.gray150};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 10px 12px 16px;
  height: 44px;
`;

const ShowMore = styled.button`
  display: flex;
  align-items: center;
  color: ${vars.color.textTertiary};
`;

const List = styled.div`
  padding: 0 16px;
`;

const Bar = styled.div`
  height: 1px;
  background: ${vars.v3Color.gray100};
`;
